/**
 * Búsqueda de alimentos (design.md, 8.12; Req. 19.5, 19.6).
 *
 * Sin acentos ni mayúsculas, con sinónimos ("jitomate" y "tomate") y con
 * prioridad para favoritos, lo frecuente y lo reciente. Código puro: la lista
 * de candidatos (base incluida y Mis alimentos) la arma quien llama.
 */

import type { MealEntry } from '@/db/types';
import { diferenciaDias, type FechaLocal } from './dates';

/** Sin marcas diacríticas, en minúsculas y sin espacios de sobra. */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function palabras(texto: string): string[] {
  return normalizar(texto)
    .split(/[^a-z0-9]+/)
    .filter((p) => p !== '');
}

export interface Candidato {
  /** 'base:ab_tortilla_maiz' o 'propio:ali_…', la misma clave que usan los usos. */
  clave: string;
  nombre: string;
  sinonimos: readonly string[];
  favorito: boolean;
}

export interface Uso {
  /** Veces registrado en los últimos 60 días. */
  veces: number;
  ultimaFecha: FechaLocal;
}

export const DIAS_FRECUENCIA = 60;
export const MAXIMO_RESULTADOS = 50;

/** Puntaje del texto: null si no coincide (design.md, 8.12). */
function puntajeTexto(consulta: string, palabrasConsulta: readonly string[], texto: string): number | null {
  const propias = palabras(texto);
  const todas = palabrasConsulta.every((q) => propias.some((p) => p.startsWith(q)));
  if (!todas) return null;
  const normal = normalizar(texto);
  if (normal === consulta) return 100;
  if (normal.startsWith(consulta)) return 80;
  return 60;
}

export function puntaje(
  consultaOriginal: string,
  candidato: Candidato,
  uso: Uso | undefined,
  hoy: FechaLocal,
): number | null {
  const consulta = normalizar(consultaOriginal);
  const palabrasConsulta = palabras(consultaOriginal);
  if (palabrasConsulta.length === 0) return null;

  let base: number | null = null;
  for (const texto of [candidato.nombre, ...candidato.sinonimos]) {
    const p = puntajeTexto(consulta, palabrasConsulta, texto);
    if (p !== null && (base === null || p > base)) base = p;
  }
  if (base === null) return null;

  let total = base;
  if (candidato.favorito) total += 30;
  if (uso !== undefined) {
    total += Math.min(uso.veces, 30);
    if (diferenciaDias(uso.ultimaFecha, hoy) < 7) total += 10;
  }
  return total;
}

/** Resultados por puntaje y luego por nombre; máximo 50. */
export function buscarAlimentos<T extends Candidato>(
  consulta: string,
  candidatos: readonly T[],
  usos: ReadonlyMap<string, Uso>,
  hoy: FechaLocal,
  limite = MAXIMO_RESULTADOS,
): T[] {
  const conPuntaje: Array<{ c: T; p: number }> = [];
  for (const c of candidatos) {
    const p = puntaje(consulta, c, usos.get(c.clave), hoy);
    if (p !== null) conPuntaje.push({ c, p });
  }
  conPuntaje.sort((a, b) => b.p - a.p || a.c.nombre.localeCompare(b.c.nombre, 'es'));
  return conPuntaje.slice(0, limite).map((x) => x.c);
}

// ---------------------------------------------------------------------------
// Recientes y frecuencia, a partir de las comidas registradas
// ---------------------------------------------------------------------------

/** La clave del origen de un registro: 'base:…', 'propio:…' o 'rapido:<nombre>'. */
export function claveDeRegistro(e: Pick<MealEntry, 'origen' | 'nombre'>): string {
  return e.origen.tipo === 'rapido' ? `rapido:${normalizar(e.nombre)}` : `${e.origen.tipo}:${e.origen.refId}`;
}

/** Veces y última fecha de cada origen en los últimos 60 días. */
export function usosDe(entradas: readonly MealEntry[], hoy: FechaLocal): Map<string, Uso> {
  const usos = new Map<string, Uso>();
  for (const e of entradas) {
    const dias = diferenciaDias(e.fecha, hoy);
    if (dias < 0 || dias >= DIAS_FRECUENCIA) continue;
    const clave = claveDeRegistro(e);
    const previo = usos.get(clave);
    usos.set(clave, {
      veces: (previo?.veces ?? 0) + 1,
      ultimaFecha: previo === undefined || e.fecha > previo.ultimaFecha ? e.fecha : previo.ultimaFecha,
    });
  }
  return usos;
}

/**
 * El último registro de cada origen, del más reciente al más antiguo: lo que
 * se ofrece para repetir en un toque (Req. 19.6).
 */
export function recientesDe(entradas: readonly MealEntry[], limite = 12): MealEntry[] {
  const ordenadas = [...entradas].sort(
    (a, b) => b.momento.localeCompare(a.momento) || b.creadoEn.localeCompare(a.creadoEn),
  );
  const vistas = new Set<string>();
  const recientes: MealEntry[] = [];
  for (const e of ordenadas) {
    const clave = claveDeRegistro(e);
    if (vistas.has(clave)) continue;
    vistas.add(clave);
    recientes.push(e);
    if (recientes.length === limite) break;
  }
  return recientes;
}
